import { ChatMessages } from 'models/ChatMessages.jsx';
import { GameRoom } from 'models/GameRoom.jsx';
import { Userlist } from 'models/Userlist.jsx';
import { Users } from 'models/UserCollection';

import moment from 'moment';
import { ladderLocalStorage } from 'components/Ladder.jsx';
import { Request } from 'components/Request.jsx';
import { ChatActions } from 'components/ChatActions.jsx';
import { Dashboard } from 'components/Dashboard';
import { MapHelper } from 'functions/MapHelper.jsx';
import { UserlistSection } from './UserlistSection';

import store from 'react/redux/store/store';
import * as chatActions from 'react/redux/actions/chat';
import { privateChatPartnerPresenceChanged } from 'react/redux/actions/privateChatPresence';
import Globals from 'components/Globals';
import StateHelpers from '../react/redux/store/StateHelpers';
import { chatRoomDescriptionUpdated } from "react/redux/actions/chat";
import { CacheableDataObject } from "./CacheableDataObject";
import { countMembersStoppedLookingSince, stoppedLookingAfter } from 'models/presence/searchActivity';
import { __ } from 'i18n/index';

export class ChatRoom extends CacheableDataObject {
	static MELEE_CHAT_ROOM = 509;

	beforeConstruct() {
		this.isLinkOnly = null;
		this.chatGroup = null;
		// The player's mute of this room: null, or { until } in unix seconds (null until unmuted).
		this.mute = null;
		this.chatMessages = ChatMessages.create();
		this.chatContainerComponent = null;

		this.chatTabComponentReferences = new Set();

		this.userlistLoading = null;

		this.defaultUserSort = this.defaultUserSort.bind(this);
		this.chatRoomUserlistRetrieveFunction = this.chatRoomUserlistRetrieveFunction.bind(this);
	}

	afterUpdate() {
		if(this.id) {
			if(!this.userlist) {
				this.userlist = new Userlist(this);
				this.userlist.setUserlistRetrieveFunction(this.chatRoomUserlistRetrieveFunction);

				this.userlist.setSections([
					new UserlistSection(__('Chat Admin'), (user) => {
						return user.chat_rooms.isAdmin(this.id) || user.is_mod;
					}, this.defaultUserSort),
					new UserlistSection(__('Mods'), (user) => {
						return user.chat_rooms.isMod(this.id);
					}, this.defaultUserSort),
					new UserlistSection(__('Players'), true,
						this.defaultUserSort)
				]);
			}
			this.chatMessages.chatRoom = this;
		} else {
			this.userlist = null;
		}
	}

	// `shownInPeopleColumn`: the People column is asking, so the list is visible
	// whatever the legacy userlist toggles say.
	chatRoomUserlistRetrieveFunction({ shownInPeopleColumn = false } = {}) {
		if(this.userlistLoadedOnce) {
			return Promise.resolve();
		}
		if(!shownInPeopleColumn && !Dashboard.userlistIsVisible()) {
			return Promise.resolve();
		}
		if(this.loadingUserlist) {
			// Callers that ask while the request is in flight wait for it too.
			return this.loadingUserlist;
		}
		this.loadingUserlist = Request.apiv1Get('chat/userlist', { chat_room_id: this.id })
			.then((response) => {

				Dashboard.performOpenSearchUpdate(response);
				this.userlist.updateUserlistDisplay();
				this.userlistLoadedOnce = true;
			})
			.finally(() => {
				this.loadingUserlist = null;
			});
		return this.loadingUserlist;
	}

	defaultUserSort([_, a], [__, b]) {
		if(a.isMe()) {
			return -1;
		}
		if(b.isMe()) {
			return 1;
		}

		let aIsMod = this.userIsModOfThisList(a);
		let bIsMod = this.userIsModOfThisList(b);
		if(aIsMod && !bIsMod) {
			return -1;
		}
		if(!aIsMod && bIsMod) {
			return 1;
		}
		let distanceA = ChatRoom.getDistanceFromMyUser(a);
		let distanceB = ChatRoom.getDistanceFromMyUser(b);
		if(distanceA === distanceB) {
			return 0;
		}
		return distanceA > distanceB ? 1 : -1;
	}

	static getDistanceFromMyUser(user) {
		if(user && user.location && user.location.distanceFromUser) {
			if(user.location.distanceFromUser.distance !== null) {
				return user.location.distanceFromUser.distance;
			}
		}
		return 100000000;
	}

	userIsModOfThisList(user) {
		return user.chat_rooms.isModOrAdmin(this.id) || user.is_mod;
	}

	changeChatLeft() {
		console.log('attempt to change left');
		let previous = null;
		let current = null;
		for(let [_, chatRoom] of ChatRoom.activeChats) {
			current = chatRoom;
			if(current === this) {
				if(previous) {
					previous.setActive();
					return previous;
				} else {
					const last = Array.from(ChatRoom.activeChats.values()).pop();
					last.setActive();
					return last;
				}
			}
			previous = chatRoom;
		}
	}

	changeChatRight() {
		let found = null;
		let current = null;
		for(let [i, chatRoom] of ChatRoom.activeChats) {
			current = chatRoom;
			if(found) {
				current.setActive();
				return current;
			}
			if(current === this) {
				found = this;
			}
		}
		let result = ChatRoom.activeChats.values().next();
		if(result.value) {
			result.value.setActive();
			return result.value;
		}
	}

	hasAdmin(user) {
		if(!user) {
			return false;
		}
		return user.chat_rooms.isAdmin(this.id) || user.isSiteMod();
	}

	hasMod(user) {
		if(!user) {
			return false;
		}
		return user.chat_rooms.isMod(this.id) || user.isSiteMod();
	}

	static getActiveChatById(chatRoomId) {
		console.error('this is not used yet..');
		return ChatRoom.activeChats(chatRoomId);
	}

	static getChatMessagesForActiveChat(chatRoomId) {
		const chatRoom = ChatRoom.activeChats.get(chatRoomId);
		if(chatRoom) {
			return chatRoom.chatMessages.getInitialChatMessages();
		} else {
			return Promise.resolve();
		}
	}

	static isInChatById(id) {
		return ChatRoom.activeChats.get(Number.parseInt(id, 10));
	}

	/**
	 * Userlist changes sent while the socket was down are lost: reload the
	 * focused room's userlist now; the other rooms reload when focused.
	 */
	static reloadUserlistsAfterReconnect() {
		ChatRoom.activeChats.forEach((chatRoom) => {
			chatRoom.userlistLoadedOnce = false;
		});
		const focusedRoom = ChatRoom.getActiveMainChat();
		if(!focusedRoom || !focusedRoom.userlist) {
			return Promise.resolve();
		}
		return focusedRoom.userlist.retrieveUserlist();
	}

	static getActiveMainChat() {
		return ChatRoom.activeChats.selectedChat ? ChatRoom.activeChats.selectedChat : null;
	}

	static aChatHasUnreadMessages() {
		for(let [_, chatRoom] of ChatRoom.activeChats) {
			if(chatRoom.chatMessages.hasUnreadMessages() && !chatRoom.isMuted()) {
				return true;
			}
		}
		return false;
	}

	static addMessageToActiveChatRoom(message) {
		const activeChat = ChatRoom.getActiveMainChat();
		if(!activeChat) {
			console.log(message);
			console.error('had no active chat to add messags to ');
			return;
		}
		activeChat.chatMessages.addNotification(message);
	}

	static retrieveChatRoomIfExists(chat) {
		const chatRoomMaybe = ChatRoom.create(chat);
		if(chatRoomMaybe.id === undefined) {
			return null;
		}
		const chatRoom = ChatRoom.activeChats.get(chatRoomMaybe.id);
		return chatRoom || null;
	}

	static updateAndRetrieveChatRoom(chat, addIfNecessary = true) {
		let chatRoomMaybe = ChatRoom.create(chat);
		if(chatRoomMaybe.id === undefined) {
			console.log('had no id', chatRoomMaybe);
		}
		let chatRoom = ChatRoom.activeChats.get(chatRoomMaybe.id);
		if(chatRoom) {
			chatRoom.update(chat);
		} else if(addIfNecessary) {
			chatRoom = chatRoomMaybe;
			// ChatRoom.activeChats.set(chatRoomMaybe.id, chatRoom);
			chatRoom.addToActiveList();

			MapHelper.inPlaceSort(ChatRoom.activeChats, ([_, a], [__, b]) => {
				return a.order > b.order ? 1 : -1;
			});
		}
		return chatRoom;
	}

	toString() {
		return `${this.name} - ${this.id}`;
	}

	/**
	 * Online members of the room (chat/my_chats, refreshed by chat/room_counts).
	 * That snapshot can lag a join or a reconnect, so it never goes below what
	 * the client knows: the members in the loaded userlist, and the current
	 * user in a room they are in.
	 */
	getHereCount() {
		const snapshotHere = this.counts ? this.counts.here : 0;
		const currentUserIsHere = ChatRoom.activeChats.get(this.id) === this ? 1 : 0;
		return Math.max(snapshotHere, this.countListedMembers(), currentUserIsHere);
	}

	/** Members in the loaded userlist (its sections never share a user). */
	countListedMembers() {
		const sections = this.userlist ? this.userlist.retrieveSections() : null;
		if(!sections) {
			return 0;
		}
		return sections.reduce((total, section) => total + section.getCount(), 0);
	}

	/**
	 * Online members with an open search that counts for this room. The
	 * snapshot can be a minute or more old, so members the client saw stop
	 * looking since it arrived are taken off. The snapshot names who it
	 * counted, so this works without the room's userlist, which only loads
	 * once the People column shows the room.
	 */
	getLookingCount() {
		if(!this.counts) {
			return 0;
		}
		const since = this.counts.receivedAt || 0;
		const lookingPlayerIds = this.counts.lookingPlayerIds;
		if(!lookingPlayerIds) {
			// A snapshot cached before it named players: fall back to the userlist.
			const stoppedSince = countMembersStoppedLookingSince(this, since);
			return Math.max(0, this.counts.looking - stoppedSince);
		}
		const ladderId = this.ladder ? this.ladder.id : null;
		const stillLooking = lookingPlayerIds.filter((playerId) => !stoppedLookingAfter(playerId, ladderId, since));
		return stillLooking.length;
	}

	static addMessagesToProperChatRoom(chat, messages, messagesAreNew) {
		const userlistData = chat.userlist;

		const chatRoom = ChatRoom.updateAndRetrieveChatRoom(chat);
		if(!chatRoom.id) {
			console.log(chat);
			console.error(chatRoom);
			throw new Error('Something went horribly horribly wrong attempting to create a chat room with no id');
		}
		if(userlistData) {
			let aMemberCameOnline = false;
			for(let i in userlistData) {
				if(!userlistData.hasOwnProperty(i)) {
					continue;
				}
				const current = userlistData[i];
				if(current.is_removed) {
					chatRoom.userlist.removeUser(i);
				} else if(current.is_presence_update) {
					// A listed member started or stopped looking or playing: merge
					// the change into them. Members not listed here are ignored.
					if(chatRoom.userlist.hasUser(current.id)) {
						const { is_presence_update: presenceUpdateFlag, ...presenceChange } = current;
						chatRoom.userlist.addUser(Users.update(presenceChange));
					}
				} else {
					const member = Users.update(current);
					// The userlist only holds connected members. The DM list's dot reads
					// is_online from when it loaded, so a member listed here is online.
					if(!member.is_online) {
						member.is_online = true;
						aMemberCameOnline = true;
					}
					chatRoom.userlist.addUser(member);
				}
			}
			chatRoom.userlist.sort();
			store.dispatch(chatActions.userlistUpdated(chatRoom.userlist));
			if(aMemberCameOnline) {
				store.dispatch(privateChatPartnerPresenceChanged());
			}
		}
		if(messages) {
			chatRoom.chatMessages.addMessages({ messages: messages, messagesAreNew: messagesAreNew });
		}
		if(chat.chat_message_reactions) {
			for(let messageId in chat.chat_message_reactions) {
				if(!chat.chat_message_reactions.hasOwnProperty(messageId)) {
					continue;
				}
				chatRoom.chatMessages.applyReactions(messageId, chat.chat_message_reactions[messageId]);
			}
		}
		if(chat.chat_message_kind_updates) {
			for(let messageId in chat.chat_message_kind_updates) {
				if(!chat.chat_message_kind_updates.hasOwnProperty(messageId)) {
					continue;
				}
				chatRoom.chatMessages.applyKindUpdate(messageId, chat.chat_message_kind_updates[messageId]);
			}
		}
		if(Globals.initialChat && chatRoom.id == Globals.initialChat) {
			Globals.initialChat = null;
			chatRoom.setActive();
		}
	}

	isActive() {
		if(this.chatGroup === ChatRoom.activeChats && this.chatGroup.selectedChat && StateHelpers.mainChatIsActive()) {
			return this.chatGroup.selectedChat.id === this.id;
		} else {
			return false;
		}
	}

	exit() {
		this.closeLocally();
		Request.apiv1('chat/leave', { chat_room_id: this.id });
	}

	isMuted() {
		if(!this.mute) {
			return false;
		}
		return this.mute.until === null || this.mute.until * 1000 > Date.now();
	}

	/** When the mute ends, in unix seconds; null when it lasts until unmuted. */
	mutedUntil() {
		return this.mute ? this.mute.until : null;
	}

	/** duration: 'hour', 'eight_hours' or 'indefinite' (services\chat\mute\RoomMuteDuration). */
	muteFor(duration) {
		return Request.apiv1('chat/mute_room', { chat_room_id: this.id, duration })
			.then((response) => this.applyMute(response.mute));
	}

	unmute() {
		return Request.apiv1('chat/unmute_room', { chat_room_id: this.id })
			.then(() => this.applyMute(null));
	}

	applyMute(mute) {
		this.mute = mute || null;
		store.dispatch(chatActions.setMyChatRoomList(ChatRoom.activeChats));
		store.dispatch(chatActions.updateGeneralChatUnreadMessageState());
	}

	/** Marks the whole room read from the room menu, whether or not its messages are loaded. */
	markRead() {
		this.chatMessages.markEverythingRead();
		store.dispatch(chatActions.updateGeneralChatUnreadMessageState());
		return Request.apiv1('chat/read_all', { chat_room_id: this.id })
			.then((response) => this.chatMessages.applyRemoteRead(Number(response.last_read_message_id)));
	}

	closeLocally() {
		if(this.isActive()) {
			this.changeChatRight();
		}
		this.chatGroup.delete(this.id);
		store.dispatch(chatActions.setMyChatRoomList(ChatRoom.activeChats));
	}

	setActive(e) {
		if(e) {
			e.preventDefault();
		}
		if(this.isLinkOnly) {
			Request.apiv1('chat/join', { id: this.id })
				.then(response => {
					Globals.initialChat = response.chat_id;
					Dashboard.performOpenSearchUpdate(response);
				})
				.catch(Request.generalJsonError);
		} else {
			if(this.chatGroup === ChatRoom.activeChats) {
				store.dispatch(chatActions.setChatRoomAsActive(this));
			}
		}
		return this;
	}

	getEditUrl() {
		return siteUrl + '/chats/manage?room=' + this.id;
	}

	getInviteUrl() {
		return siteUrl + '/chat/invite/' + this.id;
	}

	getUrl() {
		return siteUrl + '/netplay/' + this.id;
	}

	setChatGroup(group) {
		this.chatGroup = group;
	}

	addToList(cache) {
		this.setChatGroup(cache);

		// So that the chat can be used for other display purposes.
		ChatRoom.retrieve(
			{
				id: this.id,
				name: this.name
			}
		);
		cache.set(this.id, this);
		store.dispatch(chatActions.setMyChatRoomList(ChatRoom.activeChats));
		if(Dashboard.chatRoomsLoaded && !cache.selectedChat && cache === ChatRoom.activeChats) {
			this.setActive();
		}
	}

	hasDescription() {
		return !!(this.description || this.description_id);
	}

	/** The line beside the room name in its header. */
	getTopic() {
		if(this.summary_description) {
			return this.summary_description;
		}
		if(this.has_ladder) {
			return __('%s netplay. Post an LFG with the button in the message box.', this.name);
		}
		return null;
	}

	descriptionIsActive() {
		var chatRooms = ladderLocalStorage.getItem('chat_rooms');
		if(!this.hasDescription()) {
			return false;
		}

		if(chatRooms && chatRooms.chats && chatRooms.chats[this.id] &&
			chatRooms.chats[this.id].last_motd_id == this.description_id &&
			chatRooms.chats[this.id].last_motd_times_closed > 0) {
			return false;
		}
		return true;
	}

	descriptionToggle() {
		return this._descriptionUpdate(!this.descriptionIsActive());
	}

	descriptionClose() {
		return this._descriptionUpdate(false);
	}

	descriptionReset() {
		return this._descriptionUpdate(true);
	}

	_descriptionUpdate(reset = false) {
		var chatRooms = ladderLocalStorage.getItem('chat_rooms');
		if(!chatRooms) {
			chatRooms = {};
			chatRooms.chats = {};
		}
		if(!chatRooms.chats[this.id]) {
			chatRooms.chats[this.id] = {};
		}
		if(chatRooms.chats[this.id].last_motd_id !=
			this.id) {
			chatRooms.chats[this.id].last_motd_times_closed = 0;
		}
		if(reset) {
			chatRooms.chats[this.id].last_motd_times_closed = 0;
		} else {
			chatRooms.chats[this.id].last_motd_times_closed++;
		}
		chatRooms.chats[this.id].last_motd_id = this.description_id;

		ladderLocalStorage.setItem('chat_rooms', chatRooms);
		store.dispatch(chatRoomDescriptionUpdated());
	}


	addToActiveList() {
		this.isLinkOnly = false;
		if(ChatRoom.featuredChats.has(this.id)) {
			ChatRoom.featuredChats.get(this.id).removeFromList();
		}
		return this.addToList(ChatRoom.activeChats);
	}

	addToRecentList() {
		this.isLinkOnly = true;
		if(ChatRoom.recentChats.has(this.id)) {
			return;
		}
		return this.addToList(ChatRoom.recentChats);
	}

	addToFeaturedList() {
		this.isLinkOnly = true;
		if(ChatRoom.activeChats.has(this.id) || ChatRoom.featuredChats.has(this.id)) {
			return;
		}
		return this.addToList(ChatRoom.recentChats);
	}

	removeFromList() {
		let cache = this.chatGroup;
		cache.delete(this.id);
		if(cache === ChatRoom.activeChats) {
			store.dispatch(chatActions.setMyChatRoomList(ChatRoom.activeChats));
			this.addToRecentList();
		}
	}

}

$('#basic_chats').addClass('active');
ChatRoom.activeChats = new Map();
ChatRoom.featuredChats = new Map();
ChatRoom.recentChats = new Map();

ChatRoom.preferredChat = null;

// The server closed the room (a mod switched its ladder chat off), so drop it
// without calling chat/leave.
ChatRoom.closeFromServer = (chatRoomId) => {
	const room = ChatRoom.activeChats.get(Number.parseInt(chatRoomId));
	if(room) {
		room.closeLocally();
	}
};

// A room's palette colors changed (chat_room_colors_update); repaint its row.
ChatRoom.applyColorsFromServer = (chatRoomId, colors) => {
	const room = ChatRoom.activeChats.get(Number.parseInt(chatRoomId));
	if(room) {
		room.colors = colors;
		store.dispatch(chatActions.setMyChatRoomList(ChatRoom.activeChats));
	}
};

// This player muted or unmuted the room in another session (chat_room_mute).
ChatRoom.applyMuteFromServer = (chatRoomId, mute) => {
	const room = ChatRoom.activeChats.get(Number.parseInt(chatRoomId));
	if(room) {
		room.applyMute(mute);
	}
};

// Another session of this player read the room (chat_room_read): clear what it covers.
ChatRoom.applyReadFromServer = (chatRoomId, lastReadMessageId) => {
	const room = ChatRoom.activeChats.get(Number.parseInt(chatRoomId));
	if(room) {
		room.chatMessages.applyRemoteRead(lastReadMessageId);
		store.dispatch(chatActions.updateGeneralChatUnreadMessageState());
	}
};

ChatRoom.prototype.dataLocationParsers = {
	id: (chat, data) => {
		chat.id = Number.parseInt(data.id);
	},
	chat_messages: () => {

	},
	userlist: () => {

	},
	ladder: (chat, data) => {
		chat.ladder = GameRoom.retrieve(data.ladder);
	},
	counts: (chat, data) => {
		chat.counts = {
			here: Number(data.counts && data.counts.here) || 0,
			looking: Number(data.counts && data.counts.looking) || 0,
			lookingPlayerIds: data.counts && Array.isArray(data.counts.looking_player_ids) ? data.counts.looking_player_ids : null,
			receivedAt: Date.now(),
		};
	},
	read_state: (chat, data) => {
		chat.chatMessages.applyReadState(data.read_state);
	},
	last_update: (chat, data) => {
		chat.last_update = moment(data.last_update * 1000);
	}
};